/**
 * Job roles / careers data.
 *
 * Each role is structured so the careers page can render a consistent card
 * (summary + "what you'll do" + "you're a great fit if") and so the
 * application <select> can list every open title.
 */

export const OPEN_APPLICATION_ROLE = 'Open application: suggest a role';
export const GENERAL_INTEREST_ROLE = 'General interest';

export const jobRoles = [
  {
    title: 'Founding Developer',
    type: 'Full-time · Remote',
    category: 'Engineering',
    summary:
      'Work with the founder on the iOS app, content tools, and backend.',
    responsibilities: [
      'Ship features across the SwiftUI iOS app and the TypeScript backend on Cloudflare Workers.',
      'Build features from the data model through to the interface.',
      'Choose tools and maintain the code with the founder.',
      'Write tests, review changes, and investigate bugs.',
    ],
    qualities: [
      "You've shipped real products and can work across the stack.",
      "You can plan your work and explain technical decisions.",
      'Bonus: Swift/SwiftUI, Cloudflare Workers, or language / education tech.',
    ],
  },
  {
    title: 'Founding Designer',
    type: 'Full-time · Remote',
    category: 'Design',
    summary:
      'Design lessons and speaking practice. Maintain the components and visual guidelines used across the app.',
    responsibilities: [
      'Design onboarding, lessons, and speaking practice.',
      'Build and maintain the design system and component library.',
      'Design animations for lesson feedback and navigation.',
      'Work with the founder to plan and test product changes.',
    ],
    qualities: [
      'Strong portfolio of shipped, polished mobile product work.',
      'Experience with motion design and Rive is required.',
      'You test designs with learners and use their feedback.',
    ],
  },
  {
    title: 'UGC Creator Manager',
    type: 'Part-time · Remote',
    category: 'Creator partnerships',
    summary:
      'Recruit creators, write briefs, and coordinate short videos for Bhol.',
    responsibilities: [
      'Source, vet, and onboard heritage-speaker creators across TikTok and Instagram.',
      'Be the day-to-day point of contact: briefs, timelines, feedback, and payments.',
      'Turn product features and cultural moments into clear, repeatable creator briefs.',
      'Manage the content calendar and follow up on deadlines.',
      'Review video performance and use the results to improve future briefs.',
    ],
    qualities: [
      "You're organized, a strong communicator, and comfortable working with creators.",
      'Experience working with (or as) a creator in UGC / influencer / social is a big plus.',
      'You understand heritage and South Asian diaspora culture and audiences.',
      'Bonus: you can edit or give sharp creative feedback on short-form video.',
    ],
  },
  {
    title: 'Growth / Performance Marketer',
    type: 'Part-time / Contract · Remote',
    category: 'Marketing',
    summary:
      'Run advertising campaigns and improve how new learners get started and return to Bhol.',
    responsibilities: [
      'Plan and run paid acquisition (Meta, TikTok, Apple Search Ads) and report on CAC and ROAS.',
      'Improve App Store conversion through ASO.',
      'Build lifecycle and notification flows that lift activation and retention.',
      'Set up clean measurement and turn the data into the next set of experiments.',
    ],
    qualities: [
      "You've grown a consumer or mobile app and can show the numbers.",
      'Equally comfortable in ad managers, analytics, and a spreadsheet.',
      'You can plan, run, and evaluate a campaign.',
    ],
  },
  {
    title: 'Native-Speaker Reviewer',
    type: 'Contract · Remote',
    category: 'Content',
    summary:
      'Help make sure every lesson sounds like a real family member, not a textbook. We’re looking for fluent native speakers of an Indian language. Reviewers are subject to a short language assessment.',
    responsibilities: [
      'Review decks and conversation scenarios for natural, everyday phrasing.',
      'Catch tone, register, and transliteration issues before content ships.',
      'Flag anything that sounds formal, stiff, or regionally off.',
      'Suggest the way your own family would actually say it.',
    ],
    qualities: [
      'Tell us which Indian languages you speak fluently.',
      'Happy to complete a short language assessment as part of the process.',
      'A good ear for the difference between “correct” and “natural.”',
      'Reliable, detail-oriented, and comfortable with flexible contract work.',
    ],
  },
];

export default jobRoles;
